import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {environment} from '../../../environments/environment';
import {HttpClient} from '@angular/common/http';
import {Injectable, NgZone, OnDestroy} from '@angular/core';
import {NavigationStart, Router} from '@angular/router';
import {OktaAuth, TokenManager, UserClaims, TokenParams, OktaAuthOptions, Tokens} from '@okta/okta-auth-js';
import {takeUntil} from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class OktaService implements OnDestroy {

  readonly SS_ACTIVEROUTE = 'activeRoute';
  readonly SS_ERROR = 'oktaError';

  private oktaError = '';
  private oktaError$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private readonly oktaErrorPath: string;

  private oktaUserInfo$: BehaviorSubject<UserClaims> = new BehaviorSubject<UserClaims>(null);

  private readonly oktaClient: OktaAuth;
  private readonly oktaClientConfig: OktaAuthOptions;
  private readonly tokenManager: TokenManager;
  private readonly tokenParams: TokenParams;

  readonly oktaSsPrefix: string;

  private onDestroy$: Subject<void> = new Subject<void>();

  constructor(
    private httpClient: HttpClient,
    private ngZone: NgZone,
    private router: Router,
  ) {
    this.oktaErrorPath = environment.oktaConfig?.errorPath || '';
    this.oktaClientConfig = this.setConfig();
    if (this.oktaClientConfig) {
      // CLIENT, TOKENPARAMS AND TOKENMANAGER
      this.oktaClient = new OktaAuth(this.oktaClientConfig);
      this.oktaClient.start(); // as of v5, needs to be started as a service to auto-renew
      this.tokenParams = {scopes: this.oktaClientConfig.scopes};
      this.tokenManager = this.oktaClient.tokenManager;
      this.tokenManager.on('error', () => {
        // auto-renew failed, usually cuz user deleted cookies or something (no more valid okta session), so sign out
        this.oktaClient.signOut().catch((err) => console.log(err));
        }
      );
      // SET STORAGE PREFIX
      this.oktaSsPrefix = environment.hasOwnProperty('storagePrefix') ? environment['storagePrefix'] : this.oktaClientConfig.clientId + '-';
      // SUBSCRIBE TO ROUTING
      this.onRouting();
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  // ********************************************************************************************************
  // GENERIC STUFF
  // ********************************************************************************************************

  private setConfig(): OktaAuthOptions {
    if (
      !environment.oktaConfig?.clientId || !environment.oktaConfig?.tenantId || !environment.oktaConfig?.url
    ) {
      this.updateOktaError('Your environment.oktaConfig needs to have at least clientId, tenantId and url');
      return null;
    }
    return {
      clientId: environment.oktaConfig.clientId,
      issuer: environment.oktaConfig.url + '/oauth2/' + environment.oktaConfig.tenantId,
      pkce: true,
      redirectUri: environment.oktaConfig['withPath'] ? window.location.origin + window.location.pathname : window.location.origin,
      responseType: ['id_token', 'token'],
      scopes: ['openid', 'email'].concat(environment.oktaConfig.additionalScopes || []),
      tokenManager: {storage: 'sessionStorage', expireEarlySeconds: 300},
      transactionManager: {
        saveNonceCookie: false,
        saveParamsCookie: false,
        saveStateCookie: false,
      },
    };
  }

  private onRouting(): void {
    this.router.events
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (event) => {
          if (event instanceof NavigationStart) {
            if (event.url !== '/' && event.url !== this.oktaErrorPath) {
              this.setActiveRoute(event.url);
            }
            // BECAUSE WE HANDLE THE CALLBACK BEFORE ROUTING, THE HIDDEN IFRAME REMAINS EMPTY
            this.handleRedirect();
          }
        });
  }

  // ********************************************************************************************************
  // OKTA STUFF
  // ********************************************************************************************************

  private handleRedirect(): void {
    if (this.oktaClient.token.isLoginRedirect() || window.location.search.includes('error=')) {
      // IS REDIRECT OR OKTA ERROR
      this.updateOktaError('');
      this.parseFromUrl();
    } else if (this.oktaError === '') {
      // IS REGULAR NAVIGATION: CHECK USER INFO
      this.getUserInfoFromOkta();
    }
  }

  private parseFromUrl() {
    this.oktaClient.token.parseFromUrl()
      .then((tokenResponse) => {
        this.tokenManager.setTokens({accessToken: tokenResponse.tokens.accessToken});
        // NOW SET USER INFO FROM OKTA, INCLUDED IN THE ID TOKEN OF THIS RESPONSE
        // IT'S THE USER INFO OBSERVABLE THAT OTHER COMPONENTS OR SERVICES SHOULD USE AS INDICATION THAT ALL IS GOOD TO GO
        this.getUserInfoFromOkta();
      })
      .catch((err) => {
        this.updateOktaError(err.message || err);
      });
  }

  private getUserInfoFromOkta(): void {
    if (this.getUserInfo()) {
      return;
    }
    // MAKE CALL DIRECTLY INSTEAD OF USING THE TOKEN.GETUSERINFO, ELSE ID-TOKEN NEEDS TO BE PERSISTED AND AUTO-RENEWED JUST FOR THAT
    this.httpClient.get<UserClaims>(this.oktaClientConfig.issuer + '/v1/userinfo')
      .subscribe(
        (userInfo) => {
          this.router.navigate([this.getActiveRoute() || '/']);
          this.newUserInfo(userInfo);
        },
        () => {
          this.getTokenWithRedirect();
        }
      );
  }

  private getTokenWithRedirect(): void {
    this.oktaClient.token.getWithRedirect(this.tokenParams)
      .catch((e) => console.log(e));
  }

  getCachedAccessToken(): Promise<Tokens> {
    // TO BE USED IN CANACTIVE AND/OR CANLOAD AND/OR HTTP INTERCEPTOR
    if (this.tokenManager) {
      return this.tokenManager.getTokens();
    } else {
      console.log('should never happen: tokenmanager is null! Nothing left to do but redirect');
      this.getTokenWithRedirect();
    }
    return Promise.resolve(null);
  }

  updateOktaError(newState: string): void {
    this.oktaError = newState.toLowerCase();
    sessionStorage.setItem(this.oktaSsPrefix + this.SS_ERROR, this.oktaError);
    this.newOktaError();
    if (this.oktaError !== '') {
      if (this.oktaErrorPath) {
        this.ngZone.run(() => this.router.navigate([this.oktaErrorPath]));
      } else {
        window.alert('Bumped into an error: ' + this.oktaError);
      }
    }
  }

  forceRetry(): void {
    // METHOD THAT CAN BE CALLED BY WHICHEVER COMPONENT YOU HAVE TO HANDLE/DISPLAY OKTA ERRORS
    // FOR EXAMPLE ON A BUTTON CLICK 'TRY AGAIN' SO USER CAN FORCE AN OKTA RETRY
    this.updateOktaError('');
    this.getTokenWithRedirect();
  }

  private setActiveRoute(url: string): void {
    sessionStorage.setItem(this.oktaSsPrefix + this.SS_ACTIVEROUTE, url);
  }

  getActiveRoute(): string {
    return sessionStorage.getItem(this.oktaSsPrefix + this.SS_ACTIVEROUTE);
  }

  // ********************************************************************************************************
  // BROADCAST DATA
  // ********************************************************************************************************

  private newUserInfo(userInfo: UserClaims): void {
    this.oktaUserInfo$.next(userInfo);
  }

  onNewUserInfo(): Observable<UserClaims> {
    return this.oktaUserInfo$.asObservable();
  }

  getUserInfo(): UserClaims {
    return this.oktaUserInfo$.getValue();
  }

  private newOktaError(): void {
    this.oktaError$.next(this.oktaError);
  }

  onNewOktaError(): Observable<string> {
    return this.oktaError$.asObservable();
  }

}
